import { z } from 'zod';
import { BRANDING } from '../branding.js';

/**
 * Error reference codes surfaced to users so a report can be traced back to a code path.
 *
 * The 1E range is caller error (bad input, auth, not found), the 2E range is our fault
 * (storage, upstream service), following the FKWD reference-code convention.
 *
 * @since 1.0.0
 */
export const ERROR_CODES = {
    validation: '1E',
    unauthorized: '1E2',
    forbidden: '1E3',
    notFound: '1E4',
    conflict: '1E5',
    rateLimited: '1E6',
    signatureInvalid: '1E7',
    gone: '1E8',
    internal: '2E',
    storage: '2E2',
    upstream: '2E3',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/**
 * The reference codes as a wire enum, so an error envelope can only carry a known code.
 *
 * @since 1.3.0
 */
export const errorCodeSchema = z.enum(Object.values(ERROR_CODES));

/**
 * The single error shape every API route returns on failure.
 *
 * @since 1.0.0
 */
export const apiErrorSchema = z.object({
    code: errorCodeSchema,
    message: z.string(),
    requestId: z.string(),
    details: z.unknown().optional(),
});

export type ApiError = z.infer<typeof apiErrorSchema>;

/**
 * The response header that carries the worker's request id, quoted in error reports.
 *
 * @since 1.3.0
 */
export const REQUEST_ID_HEADER = 'x-request-id';

/**
 * What a route returns when it has done its work and has nothing else to say.
 *
 * @since 1.3.0
 */
export const okResponseSchema = z.object({
    ok: z.literal(true),
});

/**
 * The acknowledgement shape of every write route that returns no entity.
 *
 * @since 1.3.0
 */
export type OkResponse = z.infer<typeof okResponseSchema>;

/**
 * Cursor-paginated list envelope shared by every collection endpoint.
 *
 * @since 1.0.0
 * @param itemSchema - schema describing a single row
 * @returns a schema for `{ rows, nextCursor }`
 */
export function paginatedSchema<T extends z.ZodTypeAny>(itemSchema: T) {
    return z.object({
        rows: z.array(itemSchema),
        nextCursor: z.string().nullable(),
    });
}

/**
 * The cursor-paginated envelope as a type, for callers that only have the row type.
 *
 * @since 1.3.0
 */
export interface PaginatedResponse<TRow> {
    rows: TRow[];
    nextCursor: string | null;
}

/**
 * Query parameters accepted by every cursor-paginated endpoint.
 *
 * @since 1.0.0
 */
export const paginationQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
});

export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/**
 * The product slug every service, cookie, header and file name derives from.
 *
 * @since 1.3.0
 */
export const SERVICE_NAME = BRANDING.slug;

/**
 * Prefix of every custom response header the API sends.
 *
 * @since 1.3.0
 */
export const CUSTOM_HEADER_PREFIX = `x-${SERVICE_NAME}-`;
